import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchRecords,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import {
  EntityLevel,
  EsgRecord,
  EsgRecordStatus,
  ProblemDetail,
  RecordListQuery,
  RecordListResult,
  SyntiroHoneyConfidenceBand
} from '../../../types/syntiro_honey';

/**
 * The filter portion of the review list query, owned by the filter bar.
 * Pagination and sorting are layered on top by the panel.
 */
export interface ReviewFilters {
  /** Filter by review status. */
  status?: EsgRecordStatus;
  /** "Needs action" scope: exclude settled (approved/rejected) records. */
  excludeApproved?: boolean;
  /** Filter by confidence band. */
  band?: SyntiroHoneyConfidenceBand;
  /** Filter by mapping trust (EXACT / SUGGESTED / UNMAPPED). */
  mappingStatus?: EsgRecord['mappingStatus'];
  /** When true, only records flagged for follow-up. */
  needsFollowUp?: boolean;
  /** When true, only records mapped by a learned rule (the alias loop's payoff). */
  mappedFromAlias?: boolean;
  /** Filter by mapped metric definition id (free text — no lookup endpoint). */
  metricId?: string;
  /** Filter by reporting-period id (the filter-bar period selector, Maxine #11). */
  periodId?: string;
  /** Filter by source file id (set by the upload → review handoff). */
  fileId?: string;
  /**
   * Filter to records attached anywhere under this entity — the entity itself and
   * everything below it. Always set together with {@link entityLevel}.
   */
  entityId?: string;
  /** Which org level {@link entityId} names; the API needs both to match a table. */
  entityLevel?: EntityLevel;
  /**
   * Filter to records carrying ANY of these validation flags (comma-separated
   * codes) — set by the duplicate banner's "View affected records", not by the
   * filter bar.
   */
  flags?: string;
  /** Raw-label search term. */
  search?: string;
}

/** State exposed by {@link useRecordList}. */
export interface RecordListState {
  /** The current page of records, or null before the first load completes. */
  result: RecordListResult | null;
  /** Whether a list request is in flight. */
  loading: boolean;
  /** The problem detail of the last failed request, or null on success. */
  problem: ProblemDetail | null;
  /** Re-run the current query (manual refresh / post-mutation refetch). */
  refresh: () => Promise<void>;
}

/**
 * Fetch the paginated record list for the given query, refetching whenever the
 * query changes. Stale responses are discarded so rapid filter changes cannot
 * overwrite newer results. The caller must memoise `query`.
 * @param {RecordListQuery} query Filters, sort and pagination for `GET /records`.
 * @returns {RecordListState}
 */
export function useRecordList(query: RecordListQuery): RecordListState {
  const [result, setResult] = useState<RecordListResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  // Monotonic request counter: only the latest request may commit state.
  const requestSeqRef = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    requestSeqRef.current += 1;
    const requestId = requestSeqRef.current;
    setLoading(true);
    try {
      const response = await fetchRecords(query);
      if (requestSeqRef.current === requestId) {
        setResult(response);
        setProblem(null);
      }
    } catch (error: unknown) {
      if (requestSeqRef.current === requestId) {
        setResult(null);
        setProblem(toProblemDetail(error));
      }
    } finally {
      if (requestSeqRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { result, loading, problem, refresh };
}
